import { useContext, useRef, useState } from 'react';
import classNames from 'classnames';

import { media } from 'context';
import { Clickable, Icon, Layout, Modal, Text } from 'ds';
import useOnClickOutside from 'ds/helpers/useOnClickOutside';
import { actions } from 'store/CleaningService';
import { useAppDispatch } from 'store/hooks';

export interface Props<T extends string | number = string> {
  value?: T;
  options: SelectMenuOption<T>[];
  onSelect: (params: { value: T }) => void;
  placeholder?: string;
  menuPosition: 'top' | 'bottom';
}

export interface SelectMenuOption<T extends string | number> {
  value: T;
  label: string;
  disabled?: boolean;
}

const CleaningDaySelector = <T extends string | number>({
  options,
  onSelect,
  value,
  placeholder,
  menuPosition = 'top'
}: Props<T>) => {
  const dispatch = useAppDispatch();
  const { isMobile } = useContext(media);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const selectedOption = options.find(option => option.value === value);
  const containerRef = useRef<HTMLDivElement>(null);
  const [modalIsVisible, setModalIsVisible] = useState(false);
  useOnClickOutside(containerRef, () => {
    setIsOpen(false);
  });

  const handleSelect = (value: T) => {
    onSelect({ value });
    setIsOpen(false);
  };

  return (
    <Layout
      __ref={containerRef}
      position="relative"
      width="100%"
      borderWidth={1}
      borderRadius={8}
      height={48}
      align="center"
    >
      <Clickable
        onClick={e => {
          e.stopPropagation();
          if (isMobile) {
            setModalIsVisible(true);
          } else {
            setIsOpen(!isOpen);
          }
        }}
        style={{ display: 'block', paddingLeft: 4, paddingRight: 4 }}
        fullWidth
      >
        <Layout align="center" paddingX={12} justify="space-between">
          <Text size="body-sm">{selectedOption?.label || placeholder}</Text>
          <Icon name="downCaret" size="sm" />
        </Layout>
      </Clickable>
      <Layout
        __className={classNames('CleaningDaySelector-menu', { 'is-open': isOpen })}
        color="white"
        paddingY={8}
        boxShadow="0px 8px 24px 0px rgba(0, 0, 0, 0.12)"
        borderRadius={12}
        width={304}
        top={menuPosition === 'bottom' ? 52 : -260}
        height={256}
      >
        {options.map(({ value, label, disabled }) => {
          return (
            <Clickable
              onClick={e => {
                if (disabled) return;
                e.stopPropagation();
                handleSelect(value);
                dispatch(actions.setOnboardingServiceStartDate(null));
              }}
              key={value}
              fullWidth
              disabled={disabled}
              style={{
                height: '48px',
                opacity: disabled ? 0.2 : 1,
                cursor: disabled ? 'not-allowed' : 'pointer'
              }}
              hoverColor="gray-50"
            >
              <Layout paddingX={20} justify="flex-start" align="center" gap={8}>
                <Layout visibility={value === selectedOption?.value ? 'visible' : 'hidden'} marginBottom={2}>
                  <Icon name="checkmark" size="xs" stroke={2} />
                </Layout>
                <Text size="body-sm">{label}</Text>
              </Layout>
            </Clickable>
          );
        })}
      </Layout>
      <Modal
        header="Select a day of week"
        headerAlign="left"
        isVisible={modalIsVisible}
        onClose={() => setModalIsVisible(false)}
        isBottomSheet
        bottomSheetIsRounded
      >
        <Layout paddingX={24} paddingTop={8}>
          {options.map(({ value, label, disabled }) => {
            return (
              <Clickable
                onClick={e => {
                  if (disabled) return;
                  e.stopPropagation();
                  handleSelect(value);
                  setModalIsVisible(false);
                  dispatch(actions.setOnboardingServiceStartDate(null));
                }}
                key={value}
                fullWidth
                style={{
                  height: isMobile ? 56 : undefined,
                  opacity: disabled ? 0.2 : 1,
                  cursor: disabled ? 'not-allowed' : 'pointer'
                }}
                disabled={disabled}
              >
                <Layout justify="space-between" align="center" gap={8}>
                  <Text size="body-sm" semibold>
                    {label}
                  </Text>
                  {value === selectedOption?.value && <Icon name="checkmark" size={28} stroke={2} />}
                </Layout>
              </Clickable>
            );
          })}
        </Layout>
      </Modal>
    </Layout>
  );
};

export default CleaningDaySelector;
